import type { BankStatementRecord, Business, BusinessContact, Contact, Contract, ContractContact, Ledger, LedgerTransaction, TrustAccount, TrustBankStatement, TrustReport } from '@prisma/client'

// INTERFACES
export interface TrustData {
  bank: string
  accountName: string
  bsb: string
  accountNumber: string
  address: {
    fullAddress: string
    streetNumber: string
    streetName: string
    postalCode: string
    suburb: string
    state: string
    country: string
    lat: number
    lng: number
    isOverseasAddress: boolean
  }
  abn: string,
  stateLicenseNumber: string,
  openedAt: Date | null
  agentId: string | null
  tradingAccountName: string
}

export interface LedgerData {
  name: string
  number: number
  contractId: string
  trustAccountId: string
}

export interface TransactionData {
  ledgerId: string
  date: Date
  type: TransactionType
  amount: number
  method: PaymentMethod
  reason?: string
  isReversed?: boolean
  reasonForReversing?: string
  reversedAt?: Date
  contactId: string
}

export interface BankTransactionData {
  trustAccountId: string
  bankStatementId: string
  ledgerTransactionId?: string
  date: Date
  type: TransactionType
  amount: number
  description: string
}
export interface BankStatementRecordData {
  date: Date
  description: string
  debit: string
  credit: string
  amount: number,
  originalDate: Date
  sourceId: string
  type: TransactionType,
}
export interface BankStatementData {
  trustAccountId: string
  name: string
  fromDate: Date
  toDate: Date
  records: BankStatementRecordData[]
}
export interface ReportData {
  trustAccountId: string
  periodMonth: number
  periodYear: number
  startDate: Date
  endDate: Date
  financialYear: number
  status: string
  bankStatementBalance: number
}

export interface ReportPdfData {
  generatedAt: Date
  period: {
    from: Date
    to: Date
    reconciledTo: Date | null
    firstCompletedOn: Date | null
  }
  trustAccount: {
    bank: string
    accountName: string
    abn: string | null
    stateLicenseNumber: string | null
    address: TrustData['address'] | null
  }
  openingBalance: number
  totalReceipts: number
  totalPayments: number
  closingCashbookBalance: number
  bankStatementBalance?: number
  unreconciled: {
    receipts: number
    payments: number
  }
  unreconciledLines: Array<{
    date: Date
    description: string
    amount: number
    type: string
  }>
  reconciledBankBalance: number | null
  variance: number | null
  ledgerSummary: Array<{
    number: number
    name: string
    openingBalance: number
    movement: number
    closingBalance: number
  }>
  receiptsJournal: Array<{
    dateEntered: Date
    dateReceived: Date
    method: string | null
    bankDescription: string
    reason: string
    onBehalfOf: string
    ledgerNumber: number
    receiptNumber: number | null
    receivedFrom: string
    amount: number
    runningTotal: number
  }>
  paymentsJournal: Array<{
    dateEntered: Date
    datePaid: Date
    method: string | null
    bankDescription: string
    reason: string
    onBehalfOf: string
    ledgerNumber: number
    paymentNumber: number | null
    paidTo: string
    amount: number
    runningTotal: number
  }>
  completedBy: string | null
}

//  ENUMS
export enum TransactionType {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT'
}

export enum PaymentMethod {
  BANK_TRANSFER = 'BANK_TRANSFER',
  CASH = 'CASH',
  CHEQUE = 'CHEQUE'
}


// OPTIONS

export const TransactionTypeOptions = [
  { label: 'Credit', value: TransactionType.CREDIT },
  { label: 'Debit', value: TransactionType.DEBIT }
]

export const PaymentMethodOptions = [
  { label: 'Bank Transfer', value: PaymentMethod.BANK_TRANSFER },
  { label: 'Cash', value: PaymentMethod.CASH },
  { label: 'Cheque', value: PaymentMethod.CHEQUE }
]


// TYPES
export interface TrustAccountType extends TrustAccount{
  _count: { Ledger: number }
  Ledger: Partial<Ledger>[] & { LedgerTransaction : LedgerTransaction } & { Contract: Partial<Contract> & { Business: Partial<Business> } }
  activeLedgers: Partial<Ledger>[]
  allLedgers: Partial<Ledger>[]
  allTransactions: LedgerTransactionType[]
  allBankStatements: TrustBankStatement[]
  unreconciledBankTransactions: BankStatementRecord[]
  existingSourceIds: string[]
  reports: TrustReport[]
  balance: number
}

export interface LedgerType extends Ledger {
  TrustAccount: Partial<TrustAccount>
  LedgerTransaction: Array<LedgerTransaction & { Ledger: Partial<Ledger> }>
  Contract: Partial<Contract> & {
    Business: Partial<Business>
    ContractContacts: {
      contactId: string
    }[]
  }
  totalDebits: number
  totalCredits: number
  balance: number
}

export interface LedgerTransactionType extends LedgerTransaction {
  Ledger: Partial<Ledger> & {
    TrustAccount?: {
      accountName: string,
      stateLicenseNumber: string,
      UserWorkSpace: {
        name: string
      }
    },
    Contract?: {
      Business: {
        name: string
      }
    }
  }
  Contact?: {
    firstName: string,
    lastName: string
  }
}

export interface BankStatementType extends TrustBankStatement {
  BankStatementRecord: BankStatementRecord[]
  TrustAccount: {
    id: string,
    accountName: string
  }
}


// ICONS

export enum TrustAccountingIcon {
    LEDGER = 'hugeicons:notebook',
    TRANSACTION = 'hugeicons:transaction',
    RECONCILE = 'hugeicons:checkmark-circle-03',
    BANK_STATEMENT = 'hugeicons:file-02',
    REPORT = 'hugeicons:file-export',
    PENDING = 'hugeicons:cancel-circle',
    RECEIPT = 'hugeicons:receipt-dollar',
    FIND_MATCH = 'hugeicons:search-list-01',
    CUSTOM_REPORT = 'hugeicons:calendar-03'
}